// AgentV3App — top-level shell for the agent quoting flow.
//
// ════════════════════════════════════════════════════════════════════
// CANONICAL AGENT-V3 CODEBASE.
//
// This is THE active source of truth for the agent-facing Plan Match
// tool. The consumer repo (robert9907/plan-match,
// apps/web/src/pages/agent-v3/) used to carry a 7-week-old refactor
// stub; W3 Fix 7 redirected planmatch.generationhealth.me/agent-v3
// → planmatch.vercel.app/agent-v3 (this app), and W4 Fix 3 deleted
// the stale consumer directory entirely.
//
// When you make a compliance-bearing change here (SOA wording, TPMO
// language, fitness-program names, recording disclosure, etc.) it does
// NOT need to be mirrored to the consumer repo — that route now just
// renders the W3-Fix-7 redirect.
// ════════════════════════════════════════════════════════════════════
//
// 7-screen flow: intake → meds → providers → priorities → compare →
// compliance → enroll. The Plans/Swipe deck collapsed into Compare,
// which now seeds slots directly from the brain's ranked scoring
// (current + top 3 brain-ranked, bench = the rest).
//
// State that lives at this layer (lifted out of individual screens so
// downstream screens stay coherent):
//   • screen           — current page id
//   • clientView       — Agent vs Client display toggle
//   • eligiblePlans    — county/state plan catalog (one fetch)
//   • brainResult      — usePlanBrain output (one ranking)
//   • priorities       — 8-toggle output from PrioritiesScreen
//
// Compliance progress derives off useSession in render.
//
// Voice calls are not handled here — the broker dials from AgentBase
// (the AgentBar "Call" button is now a deep-link to the CRM). PlanMatch
// owns screen sharing only; AgentBase owns the call. Two apps, one
// session.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useCaptureSession } from '@/hooks/useCaptureSession';
import { usePlanBrain } from '@/hooks/usePlanBrain';
import { useRankedPlans } from '@/hooks/useRankedPlans';
import {
  normalizePlanId,
  type CsnpConditionKey,
  type LibraryRankDrugMonth,
  type LibraryRankResult,
  type SwitchSuggestion,
} from '@/lib/library-client';
import { checkNetworkBatch } from '@/lib/networkCheck';
import { useResolveRxcuis } from '@/hooks/useResolveRxcuis';
import { useSession } from '@/hooks/useSession';
import { useScreenShareStore } from '@/hooks/useScreenShare';
import { eligibilityFromAgentBase, fetchClientSession } from '@/lib/agentbase';
import { buildAgentV3LisMaps } from '@/lib/lis-cap-agent-v3';
import { resolveAgentBaseDrugs } from '@/lib/resolveAgentBaseDrugs';
import { bulkLookupFormulary } from '@/lib/formularyLookup';
import { fetchPlansForClient, lastPlanTruncated } from '@/lib/planCatalog';
import { totalComplianceItems } from '@/lib/compliance';
import type { Plan } from '@/types/plans';
import type { CsnpConditionCode, Provider, StateCode } from '@/types/session';
import { csnpConditionsFromPlan, detectConditionsFromMeds, detectedToCsnp } from '@/lib/condition-detector';
import { useAgentBaseRecommend } from '@/hooks/useAgentBaseRecommend';
import { AgentBar, type ScreenId } from './AgentBar';
import { ComplianceScreen } from './ComplianceScreen';
import { CompareScreen } from './CompareScreen';
import { DisclaimersScreen } from './DisclaimersScreen';
import { DentalScreen } from './DentalScreen';
import { qualifiesForBench } from '@/lib/benchEligibility';
import { EnrollScreen } from './EnrollScreen';
import { IntakeScreen } from './IntakeScreen';
import { MedsScreen } from './MedsScreen';
import {
  buildAgentV3SyncInput,
  type ComplianceSnapshot,
  type AgentV3SessionSummary,
} from './agentbaseSync';
import { PrioritiesScreen, type PriorityKey } from './PrioritiesScreen';
import { ProvidersScreen } from './ProvidersScreen';
import {
  applyClientSeed,
  isSeedRequested,
  pickCurrentPlanForSeed,
} from './seed';
import { AGENT_V3_CSS } from './styles';
import { getLisCopays, lisCapClassFor, normalizeLisTierForClient } from '@/lib/dual-eligible';
import { PART_D_OOP_CAP } from '@/lib/plan-brain-utils';

// Pricing-pass inputs (see "Pricing pass" in AgentV3App). Module-level so the
// hook's dependency keys stay stable across renders.
const PRICING_PASS_NO_PROVIDERS: Provider[] = [];
const PRICING_PASS_ALL_CSNP: CsnpConditionKey[] = ['diabetes', 'cardio', 'copd', 'esrd'];

/** Returns the AgentBase clients.id passed in via ?clientId=… or null
 *  when the parameter is absent / blank. The AgentBase CRM links to
 *  /agent-v3?clientId=<id> so the broker lands in the quote with the
 *  right client already loaded. */
function getClientIdParam(): string | null {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('clientId');
  return v && v.trim() ? v.trim() : null;
}

/** Explicit client fields the AgentBase "Quote in Plan Match" button
 *  packs into the URL so the Client screen lands fully populated before
 *  any async hydration resolves. All optional — only the keys that are
 *  present get applied. */
interface ClientFieldParams {
  name: string | null;
  dob: string | null;
  zip: string | null;
  county: string | null;
  state: StateCode | null;
  phone: string | null;
  email: string | null;
  currentPlanId: string | null;
  currentPlanName: string | null;
}

function getClientFieldParams(): ClientFieldParams {
  const empty: ClientFieldParams = {
    name: null,
    dob: null,
    zip: null,
    county: null,
    state: null,
    phone: null,
    email: null,
    currentPlanId: null,
    currentPlanName: null,
  };
  if (typeof window === 'undefined') return empty;
  const q = new URLSearchParams(window.location.search);
  const trim = (k: string): string | null => {
    const v = q.get(k);
    return v && v.trim() ? v.trim() : null;
  };
  const rawState = trim('state');
  const stateCode: StateCode | null =
    rawState && /^[A-Z]{2}$/i.test(rawState)
      ? (rawState.toUpperCase() as StateCode)
      : null;
  return {
    name: trim('name'),
    dob: trim('dob'),
    zip: trim('zip'),
    county: trim('county'),
    state: stateCode,
    phone: trim('phone'),
    email: trim('email'),
    currentPlanId: trim('current_plan_id'),
    currentPlanName: trim('current_plan_name'),
  };
}

function hasAnyClientFieldParam(p: ClientFieldParams): boolean {
  return Boolean(
    p.name ||
      p.dob ||
      p.zip ||
      p.county ||
      p.state ||
      p.phone ||
      p.email ||
      p.currentPlanId ||
      p.currentPlanName,
  );
}

type HydrationState =
  | { kind: 'idle' }       // no clientId in URL — seed mode or empty
  | { kind: 'loading'; clientId: string }
  | { kind: 'ready'; clientId: string; clientName: string }
  | { kind: 'error'; clientId: string; message: string };

// Priority keys that map directly to extras-axis benefit_type strings.
// All PriorityKey values map 1:1 today; the Partial<Record> shape is
// kept so future non-extras toggles can be added without breaking the
// extras-derivation below.
const PRIORITY_TO_EXTRAS: Partial<Record<PriorityKey, string>> = {
  dental: 'dental',
  vision: 'vision',
  hearing: 'hearing',
  otc: 'otc',
  fitness: 'fitness',
  transportation: 'transportation',
  telehealth: 'telehealth',
  healthy_foods: 'healthy_foods',
  partb_giveback: 'partb_giveback',
};

// Default = nothing pre-toggled. With Gate 3's strict "must offer"
// elimination, a default-on dental + vision was eliminating every plan
// that didn't file BOTH benefits before the broker even saw the
// PrioritiesScreen. The broker now opts in explicitly to each extra
// the client cares about; empty = no Gate 3 filter at all.
const DEFAULT_PRIORITIES: PriorityKey[] = [];

export function AgentV3App() {
  const [screen, setScreen] = useState<ScreenId>('intake');
  const [clientView, setClientView] = useState(false);
  const [priorities, setPriorities] = useState<PriorityKey[]>(DEFAULT_PRIORITIES);

  // Bundle-version probe — visible in the browser console on first
  // mount so we can tell at a glance whether the loaded JS has the
  // empty-default fix or a cached older bundle. Expect:
  //   [agent-v3 init] DEFAULT_PRIORITIES=[] initialPriorities=[]
  // If the right side shows ['dental','vision']
  // the browser is on a stale bundle — hard-refresh.
  useEffect(() => {
    console.log(
      '[agent-v3 init] DEFAULT_PRIORITIES=',
      DEFAULT_PRIORITIES,
      'initialPriorities=',
      priorities,
    );
    // Mount-only — we don't want to log on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = useSession((s) => s.client);
  const medications = useSession((s) => s.medications);
  const providers = useSession((s) => s.providers);
  const currentPlanId = useSession((s) => s.currentPlanId);
  const setCurrentPlanId = useSession((s) => s.setCurrentPlanId);
  const setCurrentPlanName = useSession((s) => s.setCurrentPlanName);
  const updateProvider = useSession((s) => s.updateProvider);
  const checked = useSession((s) => s.complianceChecked);
  const confirmed = useSession((s) => s.disclaimersConfirmed);
  const sessionId = useSession((s) => s.sessionId);
  const startedAt = useSession((s) => s.startedAt);

  // C-SNP eligibility from the med list (Rob, 2026-10-23 — Stephaine
  // Calahan, Burke GA). The brain only puts C-SNPs in the pool when the
  // client's chronic conditions are on file (csnpConditions), and those
  // lived only in the Intake chips — a page reload or an AgentBase-hydrated
  // session dropped them, so her Devoted C-SNP vanished ("0 C-SNP").
  // When the meds clearly show a qualifying condition (diabetes, heart
  // failure / AFib, COPD), tick that chip for the broker. A chip the
  // broker unticks stays unticked for the rest of the session.
  const updateClient = useSession((s) => s.updateClient);
  const csnpAutoApplied = useRef<Set<string>>(new Set());
  useEffect(() => {
    const detected = detectedToCsnp(
      detectConditionsFromMeds(medications).filter((d) => d.confidence !== 'possible'),
    ).filter((c): c is CsnpConditionCode =>
      // Kidney/ESRD is never auto-ticked: meds like dapagliflozin and
      // furosemide read as "kidney" but an ESRD C-SNP needs dialysis — the
      // broker ticks that one himself.
      c === 'diabetes' || c === 'cardio' || c === 'copd',
    );
    const current = client.csnpConditions ?? [];
    const toAdd = detected.filter((c) => !current.includes(c) && !csnpAutoApplied.current.has(c));
    for (const c of detected) csnpAutoApplied.current.add(c);
    if (toAdd.length > 0) updateClient({ csnpConditions: [...current, ...toAdd] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medications]);

  // AgentBase write-back. Fires from CompareScreen's Enroll buttons
  // and EnrollScreen's SunFire CTA. Hook handles idempotent same-plan
  // re-clicks + auto-retry; UI never blocks waiting for it.
  const agentbaseSync = useAgentBaseRecommend();

  // Snap-to-Session: shared capture session for the whole agent-v3
  // shell. Hoisted here so IntakeScreen (trigger), MedsScreen (med
  // inbox) and ProvidersScreen (provider inbox) all observe the same
  // queue. The hook is a no-op until SnapTrigger calls capture.start().
  const capture = useCaptureSession();

  // Backfill rxcuis on any seeded / hydrated meds that lack one — the
  // formulary + drug-cost lookups all key on rxcui, so without this hook
  // the seed payload renders red badges across the board.
  useResolveRxcuis();

  // ── Hydration: AgentBase clientId vs test-fixture seed ──────────
  // Order matters: clientId wins. AgentBase deep-links the broker via
  //   /agent-v3?clientId=<numeric AgentBase clients.id>
  // and we replace any seed/persisted data with the real CRM record
  // (clients × client_medications × client_providers) so the broker
  // doesn't have to re-key Robert's fictitious phone over their actual
  // client mid-call.
  //
  // ?seed=robert remains for demos. Both are skipped when neither URL
  // param is present — the broker can hand-key data into IntakeScreen
  // exactly as before.
  const [hydration, setHydration] = useState<HydrationState>({ kind: 'idle' });

  // Carrier-prefixed plan label captured from ?current_plan_name=… on
  // mount. Used by IntakeScreen as a fallback render while the eligible
  // plan catalog is still loading — so the broker sees the plan
  // pre-selected instantly instead of an empty picker that fills in a
  // second later. Cleared once eligiblePlans resolves the real Plan row.
  const [presetCurrentPlanLabel, setPresetCurrentPlanLabel] = useState<
    string | null
  >(null);

  // Apply URL-passed client field params synchronously on first render
  // so the Client screen renders fully populated. AgentBase's "Quote in
  // Plan Match" button packs every field it has — name, dob, zip,
  // county, state, phone, email, current_plan_id, current_plan_name —
  // and the broker should land on intake with nothing left to type.
  //
  // Runs BEFORE the async clientId fetch below so we never flash an
  // empty form. The clientId fetch then layers meds + providers on top;
  // since it writes the same client fields, the values are unchanged.
  //
  // Using useState's lazy initializer guarantees this fires exactly
  // once, before paint — useEffect would let an empty form render
  // first.
  useState(() => {
    const fields = getClientFieldParams();
    if (!hasAnyClientFieldParam(fields)) return undefined;
    const store = useSession.getState();
    const patch: Parameters<typeof store.updateClient>[0] = {};
    if (fields.name) patch.name = fields.name;
    if (fields.dob) patch.dob = fields.dob;
    if (fields.zip) patch.zip = fields.zip;
    if (fields.county) patch.county = fields.county;
    if (fields.state) patch.state = fields.state;
    if (fields.phone) patch.phone = fields.phone;
    if (fields.email) patch.email = fields.email;
    // Default planType when the deep-link path runs without a clientId
    // fetch — without it the eligible-plan catalog never fires (the
    // fetch is gated on state+county+planType). 'MAPD' mirrors the
    // clientId hydration's same fallback below.
    if (!store.client.planType) patch.planType = 'MAPD';
    if (Object.keys(patch).length > 0) store.updateClient(patch);
    if (fields.currentPlanId) {
      store.setCurrentPlanId(fields.currentPlanId);
    }
    if (fields.currentPlanName) {
      setPresetCurrentPlanLabel(fields.currentPlanName);
    }
    return undefined;
  });

  useEffect(() => {
    const clientId = getClientIdParam();
    if (!clientId) {
      // No CRM hydration requested — fall through to the seed path.
      // (URL-passed field params were applied above, before render.)
      if (isSeedRequested()) applyClientSeed(useSession.getState());
      return;
    }

    setHydration({ kind: 'loading', clientId });
    const ctl = new AbortController();
    void (async () => {
      const { detail, error } = await fetchClientSession(clientId, ctl.signal);
      if (ctl.signal.aborted) return;

      if (!detail) {
        setHydration({
          kind: 'error',
          clientId,
          message: error ?? 'Client not found in AgentBase.',
        });
        return;
      }

      // Wipe any stale meds / providers from a prior client before
      // pasting the new ones in — same guard the LandingPage picker
      // uses so we never mix two clients' data.
      const store = useSession.getState();
      for (const m of store.medications) store.removeMedication(m.id);
      for (const p of store.providers) store.removeProvider(p.id);

      const c = detail.client;
      const stateCode: StateCode | null =
        c.state && /^[A-Z]{2}$/i.test(c.state)
          ? (c.state.toUpperCase() as StateCode)
          : null;
      // plan_type isn't on the AgentBase clients table; fall back to
      // MAPD (the dominant Medicare-Advantage bucket the broker quotes
      // most). Preserve whatever the LandingPage picker would have
      // inferred by reading the same plan / lead_source heuristic via
      // the agentbase lib's deriveSummary — but to avoid pulling that
      // path in here, MAPD is a safe default the broker can switch on
      // the IntakeScreen.
      const planType = 'MAPD';

      store.updateClient({
        name: c.name,
        phone: c.phone,
        dob: c.dob,
        zip: c.zip,
        county: c.county,
        state: stateCode,
        planType,
        medicaidConfirmed: false,
        email: c.email || undefined,
        mbi: c.medicare_id || undefined,
        // Medicaid category + Extra Help from the CRM, so a dual client's
        // D-SNPs and LIS pricing show without re-clicking Intake.
        ...eligibilityFromAgentBase(c),
      });
      // current_plan_id seeds the swipe deck's benchmark column when the
      // CRM has it; otherwise IntakeScreen's CurrentPlanPicker handles
      // it later. Skip annual-review auto-flip here — agent-v3 doesn't
      // surface that mode the way v4 does. Persist plan_name too so
      // CompareScreen's "current plan not in pool" plate can name it
      // even when the id doesn't resolve to a row in eligiblePlans
      // (~76% of AgentBase clients today per the 2026-08-09 audit).
      if (c.plan_id) {
        store.setCurrentPlanId(c.plan_id);
        // AgentBaseClient wire shape names it `plan` (mapped from
        // clients.plan_name in api/_lib/clientSession.ts). Preserve
        // it so CompareScreen's plate can name the incumbent even
        // when the id doesn't resolve in the county pool.
        store.setCurrentPlanName(c.plan || null);
      }

      const parseDigit = (s: string | null | undefined): number | undefined => {
        if (!s) return undefined;
        const n = parseInt(String(s).replace(/[^0-9]/g, ''), 10);
        return Number.isFinite(n) ? n : undefined;
      };
      const rawMeds = detail.medications.filter((m) => m.name.trim());
      // Land every AgentBase med in the store IMMEDIATELY with its raw
      // broker text — hydration must never wait on /api/library/drug-
      // search. Rob's 2026-08-09 handoff hung when a slow drug-search
      // fetch blocked the whole hydration. Now the Meds screen renders
      // instantly (canonical name upgrades in later); pre-resolved rows
      // land with rxcui already set. The async resolver below upgrades
      // each unresolved row to canonical name + rxcui + tier badge as
      // results arrive.
      //
      // AgentBase-row-id → store-med-id mapping so the async resolver
      // can (a) patch each row with the resolved shape and (b) fire the
      // rxcui writeback with the AgentBase row id.
      const abIdToStoreId = new Map<string, string>();
      for (const m of rawMeds) {
        const storeId = store.addMedication({
          name: m.name,
          originalName: m.name,
          rxcui: m.rxcui || undefined,
          dose: m.dose || undefined,
          form: m.form || undefined,
          frequency: m.frequency || undefined,
          tier: parseDigit(m.tier),
          quantity: m.quantity || undefined,
          refillDays: parseDigit(m.refill_days),
          pharmacyId: m.pharmacy_id ?? undefined,
          refillDate: m.refill_date || undefined,
          notes: m.notes || undefined,
          source: 'agentbase',
          // Pre-resolved rows go straight to high; everything else
          // starts low and gets upgraded when the async resolver lands.
          confidence: m.rxcui ? 'high' : 'low',
        });
        if (m.id) abIdToStoreId.set(m.id, storeId);
      }

      // Fire-and-forget async resolver + rxcui writeback. Per-drug
      // parallel fan-out (Promise.all in resolveAgentBaseDrugs) + 5s
      // per-fetch timeout in library-client.ts bounds worst-case
      // latency to (5s × queries-per-drug). A stuck query yields a
      // null-rxcui and leaves the yellow warning in place instead of
      // freezing hydration.
      void (async () => {
        const resolvedMeds = await resolveAgentBaseDrugs(
          rawMeds.map((m) => ({
            id: m.id,
            name: m.name,
            dose: m.dose || undefined,
            form: m.form || undefined,
            rxcui: m.rxcui || undefined,
          })),
          ctl.signal,
        );
        if (ctl.signal.aborted) return;
        for (const r of resolvedMeds) {
          if (!r.id) continue;
          const storeId = abIdToStoreId.get(r.id);
          if (!storeId) continue;
          if (r.hadInputRxcui) continue;
          if (!r.resolved || !r.rxcui) continue;
          store.updateMedication(storeId, {
            name: r.canonicalName,
            rxcui: r.rxcui,
            dose: r.dose ?? undefined,
            form: r.form ?? undefined,
            confidence: 'high',
          });
        }
        // ── Fire-and-forget rxcui writeback ─────────────────────────
        // Persist the newly-resolved rxcui values back to AgentBase so
        // next hydration doesn't re-do the /api/library/drug-search
        // round trips. Persistence gate (all must hold):
        //   • Row has an AgentBase id we can target
        //   • Row's CRM rxcui was null (skip the pre-resolved echo)
        //   • Resolver returned a match (r.resolved && r.rxcui)
        //   • The picked concept came from the strength-filtered pool,
        //     not the full-results fallback (r.strengthMatched)
        const persistCandidates = resolvedMeds
          .filter((r) => r.id && !r.hadInputRxcui && r.resolved && r.rxcui && r.strengthMatched)
          .map((r) => ({ id: r.id as string, rxcui: r.rxcui as string }));
        if (persistCandidates.length > 0) {
          void fetch('/api/agentbase-backfill-rxcuis', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientId, updates: persistCandidates }),
            keepalive: true,
          }).then((r) => r.json().then((body) => {
            console.info(
              `[agent-v3] rxcui backfill: requested=${persistCandidates.length} → ` +
              JSON.stringify(body?.summary ?? { error: body?.error }),
            );
          })).catch((err) => {
            console.warn('[agent-v3] rxcui backfill failed (non-fatal):', err?.message ?? err);
          });
        }
      })();

      for (const p of detail.providers) {
        if (!p.name.trim()) continue;
        // Pre-seed networkStatus for the plan the CRM last verified
        // against, so the Providers screen renders that row as the
        // resolved status instead of flashing Checking → Verified again.
        const seededStatus: Record<string, 'in' | 'out' | 'unknown'> = {};
        if (p.last_known_plan_id && p.last_known_network_status) {
          seededStatus[p.last_known_plan_id] = p.last_known_network_status;
        }
        store.addProvider({
          name: p.name,
          specialty: p.specialty || undefined,
          npi: p.npi || undefined,
          address: p.address || undefined,
          phone: p.phone || undefined,
          source: 'manual',
          networkStatus: seededStatus,
        });
      }
      console.info(
        `[agent-v3] hydrated from AgentBase: ${detail.medications.length} meds, ${detail.providers.length} providers`,
      );

      setHydration({ kind: 'ready', clientId, clientName: c.name });
    })();

    return () => ctl.abort();
  }, []);

  // Screen-share store — collapses the spec's three-way cycle to start/stop.
  const shareActive = useScreenShareStore((s) => Boolean(s.active));
  const shareStarting = useScreenShareStore((s) => s.starting);
  const shareError = useScreenShareStore((s) => s.error);
  const shareResult = useScreenShareStore((s) => s.result);
  const startShare = useScreenShareStore((s) => s.start);
  const stopShare = useScreenShareStore((s) => s.stop);

  // ── Eligible plan catalog ────────────────────────────────────────
  // planType is intentionally null here — the broker workflow needs the
  // full county pool (MAPD + D-SNP + C-SNP + MA-only) on the compare
  // bench so the D-SNP / C-SNP / VA category filters can partition real
  // rows. Filtering by client.planType at the API would drop the SNP
  // buckets before they ever reach Bench. The brain's gates still cull
  // by client eligibility; this just widens the pool feeding them.
  const [eligiblePlans, setEligiblePlans] = useState<Plan[]>([]);
  // Truncation banner state — /api/plans hit its row cap and returned a
  // partial county universe. Bench surfaces this so the broker never
  // silently renders a partial pool.
  const [poolTruncated, setPoolTruncated] = useState(false);
  // Missing-geo banner state — the client record hydrated from
  // AgentBase without a resolvable state/county pair. Fixed the Teresa
  // Partin case (zip=27603, county='') by resolving via /api/zip-county
  // after AgentBase hydration; this banner covers the residual case
  // where zip is ALSO empty and there's nothing to resolve from.
  const [poolMissingGeo, setPoolMissingGeo] = useState<
    { reason: 'no-state' | 'no-county' } | null
  >(null);
  // Race guard — every fetch bumps the generation; only the newest
  // fetch's response is allowed to write eligiblePlans. Prevents the
  // AgentBase-hydrate race where an in-flight fetch(state=NC, county='')
  // could land AFTER a re-fired fetch(state=NC, county='Wake') and
  // overwrite the correct pool with the truncated 45-plan slice.
  const planFetchGen = useRef(0);
  useEffect(() => {
    // Refuse to fire until we have both. Empty string is missing.
    const stateOk = Boolean(client.state && client.state.trim());
    const countyOk = Boolean(client.county && client.county.trim());
    if (!stateOk || !countyOk) {
      setEligiblePlans([]);
      setPoolTruncated(false);
      setPoolMissingGeo({ reason: !stateOk ? 'no-state' : 'no-county' });
      return;
    }
    setPoolMissingGeo(null);
    const my = ++planFetchGen.current;
    const ctl = new AbortController();
    fetchPlansForClient({
      state: client.state,
      county: client.county,
      planType: null,
      signal: ctl.signal,
    })
      .then((plans) => {
        // Race guard — a newer fetch has already superseded us; drop
        // this response even though our AbortController wasn't fired
        // (both effects can run to completion before the newer cleanup
        // aborts us).
        if (my !== planFetchGen.current) return;
        setEligiblePlans(plans);
        setPoolTruncated(lastPlanTruncated());
      })
      .catch((err) => {
        if (my !== planFetchGen.current) return;
        // MissingGeoError should never fire here — the guard above
        // catches empty geo before we call fetchPlansForClient — but if
        // it does, log loudly. AbortError is expected on cleanup and
        // silently ignored.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.error('[agent-v3] fetchPlansForClient failed:', err);
      });
    return () => {
      ctl.abort();
    };
  }, [client.state, client.county]);

  // AgentBase hydration follow-up: if the CRM record carried a zip but
  // no county (~10 clients as of 2026-08-12), resolve the county from
  // /api/zip-county so the pool query above doesn't stay indefinitely
  // idle. IntakeScreen has the same resolver but only fires while the
  // broker is on that screen — the AgentBase deep-link path skips
  // Intake entirely, which is how Teresa Partin's 45-plan bug was
  // reproducing consistently.
  useEffect(() => {
    if (!client.zip || !/^\d{5}$/.test(client.zip)) return;
    if (client.county && client.county.trim()) return;
    const ctl = new AbortController();
    (async () => {
      try {
        const res = await fetch(`/api/zip-county?zip=${client.zip}`, { signal: ctl.signal });
        if (!res.ok) return;
        const body = (await res.json()) as { county?: string | null; state?: string | null };
        if (ctl.signal.aborted) return;
        const patch: Partial<typeof client> = {};
        if (body.county) patch.county = body.county;
        if (body.state && /^[A-Z]{2}$/i.test(body.state) && !client.state) {
          patch.state = body.state.toUpperCase() as StateCode;
        }
        if (Object.keys(patch).length > 0) {
          console.info(
            `[agent-v3] zip-county fallback resolved zip=${client.zip} → county=${body.county ?? 'null'}`,
          );
          useSession.getState().updateClient(patch);
        }
      } catch {
        // Best-effort — if the resolver fails, the missing-geo banner
        // still surfaces via the pool effect above.
      }
    })();
    return () => ctl.abort();
  }, [client.zip, client.county, client.state]);

  // C-SNP eligibility from the CURRENT plan (Rob, 2026-11-03 — James
  // Daniel, on UHC Complete Care GA-3 C-SNP, saw "0 C-SNP"). A client
  // already enrolled in a C-SNP qualifies, so tick the chips for that
  // plan's CMS-filed conditions. Shares csnpAutoApplied with the med
  // path, so a chip the broker unticks stays unticked.
  const currentPlanRow = useMemo(() => {
    if (!currentPlanId) return null;
    const target = normalizePlanId(currentPlanId);
    return eligiblePlans.find((p) => normalizePlanId(p.id) === target) ?? null;
  }, [currentPlanId, eligiblePlans]);
  useEffect(() => {
    if (!currentPlanRow) return;
    const fromPlan = csnpConditionsFromPlan(
      currentPlanRow.snp_type,
      currentPlanRow.csnp_condition_type,
    ) as CsnpConditionCode[];
    const current = useSession.getState().client.csnpConditions ?? [];
    const toAdd = fromPlan.filter((c) => !current.includes(c) && !csnpAutoApplied.current.has(c));
    for (const c of fromPlan) csnpAutoApplied.current.add(c);
    if (toAdd.length > 0) updateClient({ csnpConditions: [...current, ...toAdd] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlanRow]);

  // Phase 2 of the test seed — once eligiblePlans has landed, pick a
  // plausible current plan so the swipe deck has something to benchmark
  // against. Skips when (a) ?seed=robert isn't set, (b) the broker
  // already chose a current plan, (c) the catalog is still loading, or
  // (d) we're hydrating from AgentBase — the CRM's plan_id wins, and
  // even if it's blank we'd rather show CurrentPlanPicker than slap a
  // demo Aetna pick onto a real client.
  useEffect(() => {
    if (getClientIdParam()) return;
    if (!isSeedRequested()) return;
    if (currentPlanId) return;
    if (eligiblePlans.length === 0) return;
    const pick = pickCurrentPlanForSeed(eligiblePlans);
    if (pick) {
      setCurrentPlanId(pick.id);
      setCurrentPlanName(pick.plan_name);
    }
  }, [eligiblePlans, currentPlanId, setCurrentPlanId, setCurrentPlanName]);

  // ── Plan Brain ranking (single pass for the whole shell) ─────────
  const userPriorityKeys = useMemo(
    () =>
      priorities
        .map((p) => PRIORITY_TO_EXTRAS[p])
        .filter((s): s is string => !!s),
    [priorities],
  );
  const brain = usePlanBrain({
    plans: eligiblePlans,
    client,
    medications,
    providers,
    userPriorities: userPriorityKeys,
  });

  // Library-side ranking — the new source of truth for the compare
  // screen's plan list, gate results, ribbons, per-plan medication
  // coverage, and per-plan provider network status. Runs alongside
  // the legacy usePlanBrain() call: QuoteDeliveryV4 + AgentBase
  // recommend still consume `brain` (PlanBrainResult/PlanBrainData
  // shape) because the library doesn't yet expose archetype, weights,
  // applied broker rules, red flags, real-cost breakdown, or
  // structured formulary rows. Those callers move over once the
  // library response is expanded. Until then we pay one extra
  // ranking call per quote — temporary cost while the migration is
  // staged.
  // Pharmacy channel the broker can toggle on Compare. Default preferred
  // retail (what the client pays at a standard 30-day pickup); mail order
  // re-prices for 90-day fills. Lives here (not in CompareScreen) so the
  // selection drives the useRankedPlans re-fetch.
  const [pharmacyChannel, setPharmacyChannel] = useState<'preferred_retail' | 'mail_order'>('preferred_retail');
  const ranked = useRankedPlans({
    client,
    medications,
    providers,
    userPriorities: userPriorityKeys,
    csnpConditions: client.csnpConditions,
    currentPlanId,
    pharmacy: pharmacyChannel,
    // When the broker flagged the client as dual-eligible on Intake,
    // pass it through so the library's filterPlanPool keeps D-SNPs in
    // the Top-4 candidate pool. When false/undefined, the bench still
    // surfaces every D-SNP (sourced from eligiblePlans), but Top 4
    // stays strict — broker can drag any D-SNP onto the board manually
    // if the situation warrants it.
    dsnpEligible: client.dsnpEligible,
    grossMonthlyByRxcui: brain.grossMonthlyByRxcui,
  });

  // The Compare screen's drug costs come from the library's rank-plans
  // response (served by the consumer app). The agent holds the authoritative
  // publication signal locally via /api/plan-brain-data (brain.data). When the
  // catalog year's formulary isn't published yet AND the client takes drugs, we
  // must NOT display the rank-plans drug tiers/costs — they'd be a cross-year
  // guess or $0. Gate the Compare UI client-side on this flag.
  const formularyUnavailable =
    brain.data?.formularyPublished === false && medications.length > 0;
  const unpublishedPlanYear = brain.data?.planYear ?? null;

  // ── Provider network hydration: full-county direct call ───────────
  // Calls /api/library/provider-network (via checkNetworkBatch) for
  // every (NPI × eligiblePlan) pair so the broker sees in/out/unknown
  // across all ~34 county plans on ProvidersScreen — not just the
  // ~10-12 plans that survive rank-plans Gate 1. rank-plans drops any
  // plan where ANY provider is out-of-network from its response, so a
  // gate-1 hydration alone left the OON plans permanently "Unverified"
  // and the broker couldn't see why the carrier was filtered out.
  //
  // Safe to run alongside the rank-plans-driven hydration below: both
  // write to provider.networkStatus[planId] but pull from the same
  // pm_provider_network_cache + FHIR live source, so the values
  // agree. The rank-plans effect is kept because its fhir_live rows
  // can land slightly fresher (rank-plans calls provider-network
  // server-side as part of Gate 1) and idempotent overwrites are
  // harmless.
  //
  // Dependency key is the NPI list (joined string) rather than
  // `providers` itself, because we re-fire only when the set of NPIs
  // changes — not when networkStatus updates on each provider, which
  // would cause a write→read loop with updateProvider.
  const providerNpiKey = useMemo(
    () => providers.map((p) => p.npi ?? '').join('|'),
    [providers],
  );

  useEffect(() => {
    if (eligiblePlans.length === 0) return;
    if (!client.state || !client.county) return;
    const npis = providers
      .map((p) => ({ id: p.id, npi: p.npi }))
      .filter((p): p is { id: string; npi: string } => !!p.npi);
    if (npis.length === 0) return;

    let cancelled = false;
    const ctx = { state: client.state, county: client.county };

    for (const { id: providerId, npi } of npis) {
      checkNetworkBatch(npi, eligiblePlans, ctx)
        .then((map) => {
          if (cancelled) return;
          // Snapshot the freshest provider record before merging — the
          // closure-captured `providers` array may be stale by the
          // time the async response lands. Walk by id to find current.
          const current = providers.find((p) => p.id === providerId);
          const prev = current?.networkStatus ?? {};
          const next: Record<string, 'in' | 'out' | 'unknown'> = { ...prev };
          let changed = false;
          let inN = 0;
          let outN = 0;
          let unkN = 0;
          for (const [planId, result] of map) {
            if (next[planId] !== result.status) {
              next[planId] = result.status;
              changed = true;
            }
            if (result.status === 'in') inN += 1;
            else if (result.status === 'out') outN += 1;
            else unkN += 1;
          }
          console.log(
            `[agent-v3] full-county hydration npi=${npi}: ` +
              `eligible=${eligiblePlans.length} resolved=${map.size} ` +
              `→ in=${inN} out=${outN} unknown=${unkN} (changed=${changed})`,
          );
          if (changed) updateProvider(providerId, { networkStatus: next });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          console.warn(
            `[agent-v3] full-county hydration failed for npi=${npi}:`,
            err instanceof Error ? err.message : err,
          );
        });
    }
    return () => {
      cancelled = true;
    };
    // Intentionally tracks providerNpiKey, not `providers`. See note
    // above about the write→read loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligiblePlans, client.state, client.county, providerNpiKey]);

  // ── Provider network hydration: rank-plans confirmer ─────────────
  // Mirror the per-plan provider network status from the rank-plans
  // response into useSession.providers[*].networkStatus. This only
  // covers the Gate-1-survivor subset (typically 10-12 of 34 plans);
  // the full-county hydration above handles the rest.
  useEffect(() => {
    if (!ranked.result) return;
    const allPlans = [
      ...ranked.result.top_plans,
      ...ranked.result.bench_plans,
    ];
    // Resolve library triples to the agent's canonical Plan.id so the
    // networkStatus map keys match what ProvidersScreen iterates
    // (row.plan.id, from fetchPlansForClient). Without normalization
    // a library "H1234-005-0" wouldn't collide with the agent's
    // "H1234-005-000" and every row would render as "Unverified".
    const agentIdByNormalized = new Map<string, string>();
    for (const p of eligiblePlans) agentIdByNormalized.set(normalizePlanId(p.id), p.id);
    for (const provider of providers) {
      if (!provider.npi) continue;
      const next: Record<string, 'in' | 'out' | 'unknown'> = {
        ...(provider.networkStatus ?? {}),
      };
      let changed = false;
      let foundInLibrary = 0;
      let inN = 0;
      let outN = 0;
      let unkN = 0;
      for (const lp of allPlans) {
        const agentPlanId = agentIdByNormalized.get(normalizePlanId(lp.plan_id));
        if (!agentPlanId) continue;
        const row = lp.providers.find((pr) => pr.npi === provider.npi);
        if (!row) continue;
        foundInLibrary += 1;
        const status: 'in' | 'out' | 'unknown' =
          row.in_network === true
            ? 'in'
            : row.in_network === false
              ? 'out'
              : 'unknown';
        if (status === 'in') inN += 1;
        else if (status === 'out') outN += 1;
        else unkN += 1;
        if (next[agentPlanId] !== status) {
          next[agentPlanId] = status;
          changed = true;
        }
      }
      console.log(
        `[agent-v3] rank-plans hydration npi=${provider.npi}: ` +
          `library-plans=${allPlans.length} found=${foundInLibrary} ` +
          `→ in=${inN} out=${outN} unknown=${unkN} ` +
          `(changed=${changed})`,
      );
      if (changed) updateProvider(provider.id, { networkStatus: next });
    }
    // Intentionally not depending on `providers` — we only react to
    // library result refreshes. Re-running on every providers update
    // would create a write→read loop with updateProvider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranked.result, eligiblePlans]);

  // ── Formulary cache prime (shell-level) ──────────────────────────
  // MedsScreen primes pm_formulary entries for eligiblePlans × user
  // rxcuis, but that screen may be skipped (AgentBase hydration lands
  // directly on swipe, ?seed flows, etc.). Re-prime here so the
  // formulary-based sanity layer below has the data it needs to
  // override implausible pm_drug_cost_cache values.
  useEffect(() => {
    if (eligiblePlans.length === 0 || medications.length === 0) return;
    const rxcuis = medications
      .map((m) => m.rxcui)
      .filter((s): s is string => !!s);
    if (rxcuis.length === 0) return;
    const contractIds = [...new Set(eligiblePlans.map((p) => p.contract_id))];
    void bulkLookupFormulary(contractIds, rxcuis);
  }, [eligiblePlans, medications]);

  // ── Brain derivatives, sourced from the library result ─────────
  // The library's rank-plans response carries per-plan medications,
  // gate_results, ribbons, and totals. We adapt back into the side
  // maps + Plan[] shape CompareScreen already consumes so the screen
  // stays untouched while the underlying pipeline collapses to one
  // HTTP call. Plan objects come from `eligiblePlans` (the county
  // catalog) keyed by the library's `plan_id` triple.
  type DrugRow = {
    rxcui: string;
    name: string;
    covered: boolean;
    tier: number | null;
    monthlyCopay: number | null;
    annualCost: number;
    // True when the catalog year's formulary isn't published yet — the row has
    // no real tier or cost; render "formulary not yet published", never $0 /
    // Tier / Not covered.
    costUnavailable?: boolean;
    // Jan..Dec member cost for this drug (rank-plans monthly_cost).
    monthlyCost?: number[] | null;
    // Part D / Part B coverage classification (rank-plans). Drives the three
    // drug-coverage labels rendered by CompareScreen / BoardComparisonView.
    notPartD?: boolean;
    partBEligible?: boolean;
    partBOrD?: boolean;
    partBNote?: string | null;
  };

  // planById is keyed by the normalized triple form so the agent's
  // Plan.id ("H1234-005-000" — /api/plans pads empty segments) collides
  // with the library's raw triple ("H1234-005-0" — pm_plans.segment_id
  // emitted as-is). Without normalization the lookups silently miss and
  // CompareScreen falls into the "Brain ranking hasn't returned any
  // plans yet" empty state even when the library returned real plans.
  const planById = useMemo(() => {
    const m = new Map<string, Plan>();
    for (const p of eligiblePlans) m.set(normalizePlanId(p.id), p);
    return m;
  }, [eligiblePlans]);

  // One-shot diagnostic the first time the library returns plans —
  // prints both id formats side-by-side so future format drift surfaces
  // immediately in the console instead of silently emptying the deck.
  useEffect(() => {
    if (!ranked.result || eligiblePlans.length === 0) return;
    const agentSample = eligiblePlans.slice(0, 3).map((p) => p.id);
    const librarySample = ranked.result.top_plans
      .slice(0, 3)
      .map((lp) => lp.plan_id);
    const hits = ranked.result.top_plans.filter((lp) =>
      planById.has(normalizePlanId(lp.plan_id)),
    ).length;
    console.log(
      '[agent-v3 planById] agent.id sample=',
      agentSample,
      'library.plan_id sample=',
      librarySample,
      `top_plans hit-rate=${hits}/${ranked.result.top_plans.length}`,
    );
  }, [ranked.result, eligiblePlans, planById]);

  const scoredPlans = useMemo<Plan[]>(() => {
    if (!ranked.result) return [];
    return ranked.result.top_plans
      .map((lp) => planById.get(normalizePlanId(lp.plan_id)))
      .filter((p): p is Plan => p != null);
  }, [ranked.result, planById]);

  // Authoritative sellability from the library brain — UHC/Devoted flags
  // + exclude rules + Wellcare allowlist, richer than /api/plans' exclude-
  // only non_commissionable tag. Keyed by the agent's Plan.id (via the
  // same normalizePlanId collision fix the network-status mirror uses).
  // Drives the enroll block (onRecommend) and the "Not sellable" bench
  // marker. Top-4 plans are always sellable (the brain excludes
  // non-sellable from the picks), so only the bench is ever flagged.
  const librarySellableByAgentId = useMemo<Map<string, boolean>>(() => {
    const m = new Map<string, boolean>();
    if (!ranked.result) return m;
    const agentIdByNormalized = new Map<string, string>();
    for (const p of eligiblePlans) agentIdByNormalized.set(normalizePlanId(p.id), p.id);
    for (const lp of [...ranked.result.top_plans, ...ranked.result.bench_plans]) {
      if (lp.sellable == null) continue;
      const agentId = agentIdByNormalized.get(normalizePlanId(lp.plan_id));
      if (agentId) m.set(agentId, lp.sellable);
    }
    return m;
  }, [ranked.result, eligiblePlans]);

  // Bench = the broker's workspace, not the brain's runners-up: every
  // county plan the client QUALIFIES FOR (src/lib/benchEligibility.ts —
  // MAPD with drugs always; MA-only only with VA/TRICARE drug coverage;
  // D-SNP only when on Medicare + Medicaid; C-SNP only with a confirmed
  // condition, I-SNP only institutional/HCBS; never PDP), minus what's
  // already in Top 4. Rob, Oct 17 2026: don't show plans the client
  // doesn't qualify for (D-SNPs were leading a non-dual client's bench).
  // Plans the brain didn't score won't have annualDrug / ribbon / gate
  // entries; CompareScreen already renders "—" for those, which reads
  // correctly as "unscored candidate."
  const benchPlans = useMemo<Plan[]>(() => {
    if (eligiblePlans.length === 0) return [];
    const topIds = new Set(scoredPlans.map((p) => p.id));
    const benchClient = {
      dsnpEligible: client.dsnpEligible,
      csnpConditions: client.csnpConditions,
      livingSetting: client.livingSetting,
      hasVaDrugCoverage: client.hasVaDrugCoverage,
      medicaidLevel: client.medicaidLevel,
    };
    const out = eligiblePlans
      .filter((p) => !topIds.has(p.id))
      .filter((p) => qualifiesForBench(p, benchClient))
      // Mark non-sellable bench plans so CompareScreen badges them and
      // enroll is blocked. The library sellability (UHC/Devoted/Wellcare
      // allowlist) supersedes the exclude-only non_commissionable tag.
      .map((p) =>
        librarySellableByAgentId.get(p.id) === false && !p.non_commissionable
          ? { ...p, non_commissionable: true }
          : p,
      );
    if (ranked.result) {
      console.log(
        '[agent-v3] bench:',
        out.length,
        'of',
        eligiblePlans.length,
        'county plans (Top 4:',
        scoredPlans.length,
        'brain-ranked:',
        ranked.result.top_plans.length + ranked.result.bench_plans.length,
        ')',
      );
    }
    return out;
  }, [
    eligiblePlans,
    scoredPlans,
    ranked.result,
    librarySellableByAgentId,
    client.dsnpEligible,
    client.csnpConditions,
    client.livingSetting,
    client.hasVaDrugCoverage,
    client.medicaidLevel,
  ]);

  // ── Pricing pass: every plan on the board gets real drug prices ──
  // The ranking call above only prices the plans in the brain's pool for
  // this client (D-SNPs drop out unless the Intake dual box is ticked,
  // C-SNPs unless a condition qualifies), but the bench lists every plan
  // the client qualifies for. Those extra plans showed "Not priced" on
  // every drug (Juddith Eddins, Oct 30 2026 — QMB on D-SNPs). This second
  // call widens ONLY the candidate pool so the brain prices them too.
  // Nothing from it touches ranking: only per-drug coverage/cost is read,
  // and only for plans the main call didn't return.
  const mainRankedIds = useMemo(() => {
    const s = new Set<string>();
    if (!ranked.result) return s;
    for (const lp of [
      ...ranked.result.top_plans,
      ...ranked.result.bench_plans,
      ...(ranked.result.current_plan ? [ranked.result.current_plan] : []),
    ]) {
      s.add(normalizePlanId(lp.plan_id));
    }
    return s;
  }, [ranked.result]);
  const needsPricingPass =
    ranked.result != null &&
    medications.some((m) => !!m.rxcui) &&
    benchPlans.some((p) => !mainRankedIds.has(normalizePlanId(p.id)));
  // Medicaid category / LIS left off: the brain drops D-SNPs whose accepted
  // populations don't list the client's category, which would leave those
  // bench plans unpriced again. LIS caps are applied client-side below.
  const pricingClient = useMemo(
    () => ({ ...client, medicaidLevel: undefined, lisTier: undefined }),
    [client],
  );
  const pricingPass = useRankedPlans({
    client: pricingClient,
    medications,
    providers: PRICING_PASS_NO_PROVIDERS,
    csnpConditions: PRICING_PASS_ALL_CSNP,
    currentPlanId,
    pharmacy: pharmacyChannel,
    dsnpEligible: true,
    grossMonthlyByRxcui: brain.grossMonthlyByRxcui,
    enabled: needsPricingPass,
  });
  // Rank-result shape with the pricing-pass plans appended to bench_plans.
  // Used ONLY by the drug-cost maps below — ribbons, gates, sellability and
  // the Top 4 keep reading ranked.result.
  const pricedResult = useMemo<LibraryRankResult | null>(() => {
    if (!ranked.result) return null;
    const extra = pricingPass.result
      ? [
          ...pricingPass.result.top_plans,
          ...pricingPass.result.bench_plans,
          ...(pricingPass.result.current_plan ? [pricingPass.result.current_plan] : []),
        ].filter((lp) => !mainRankedIds.has(normalizePlanId(lp.plan_id)))
      : [];
    if (extra.length === 0) return ranked.result;
    return { ...ranked.result, bench_plans: [...ranked.result.bench_plans, ...extra] };
  }, [ranked.result, pricingPass.result, mainRankedIds]);

  // Extra Help per-fill maximums for this client (null = no LIS). Applied to
  // every per-drug row so a single drug's price matches the LIS-capped total.
  const lisCopays = useMemo(
    () =>
      getLisCopays(
        normalizeLisTierForClient({
          lisTier: client.lisTier,
          medicaidLevel: client.medicaidLevel,
          livingSetting: client.livingSetting,
        }),
      ),
    [client.lisTier, client.medicaidLevel, client.livingSetting],
  );

  // The by-plan-id maps below are keyed by the agent's Plan.id (NOT the
  // library's lp.plan_id) because CompareScreen looks them up as
  // `annualDrugByPlanId[plan.id]` where plan came from fetchPlansForClient.
  // Routing each library row through planById first guarantees the
  // keys line up with what the consumer reads.
  //
  // annualDrugByPlanId + dualEligibleByPlanId now co-computed: when the
  // client has LIS/Medicaid on file, the library's plan-copay drug totals
  // are re-capped client-side (see src/lib/lis-cap-agent-v3.ts) so the
  // Compare surface shows what the beneficiary actually pays. Server-side
  // LIS lands in a follow-on phase in the consumer repo; until then the
  // maps below are the source of truth for LIS-adjusted display.
  const { annualDrugByPlanId, dualEligibleByPlanId } = useMemo(
    () =>
      buildAgentV3LisMaps({
        ranked: pricedResult,
        planById,
        normalizePlanId,
        client: {
          // Retired-tier normalization — see normalizeLisTier. Passed as
          // an explicit three-field literal rather than `client` so the
          // memo's dependency list below stays exact (and lint-visible):
          // these are the only fields the normalizer reads.
          lisTier: normalizeLisTierForClient({
            lisTier: client.lisTier,
            medicaidLevel: client.medicaidLevel,
            livingSetting: client.livingSetting,
          }),
          medicaidLevel: client.medicaidLevel,
          livingSetting: client.livingSetting,
        },
      }),
    [
      pricedResult,
      planById,
      client.lisTier,
      client.medicaidLevel,
      client.livingSetting,
    ],
  );

  // Display gate: when the catalog year's formulary isn't published yet, the
  // per-plan drug totals from rank-plans are a cross-year guess. Null them all
  // so every downstream total renders incomplete ("—") rather than a figure
  // that silently excludes (or fabricates) drug cost. Inert for 2026.
  const annualDrugByPlanIdForDisplay = useMemo<Record<string, number | null>>(() => {
    if (!formularyUnavailable) return annualDrugByPlanId;
    const out: Record<string, number | null> = {};
    for (const k of Object.keys(annualDrugByPlanId)) out[k] = null;
    return out;
  }, [annualDrugByPlanId, formularyUnavailable]);

  const benchGateResultsByPlanId = useMemo<
    Record<string, { gate1_passed: boolean; gate2_passed: boolean; gate3_passed: boolean }>
  >(() => {
    if (!ranked.result) return {};
    const out: Record<string, { gate1_passed: boolean; gate2_passed: boolean; gate3_passed: boolean }> = {};
    for (const lp of ranked.result.bench_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.gate_results;
    }
    return out;
  }, [ranked.result, planById]);

  const rankedPlanIds = useMemo<string[]>(
    () => scoredPlans.map((p) => p.id),
    [scoredPlans],
  );

  const ribbonByPlanId = useMemo<Record<string, string | null>>(() => {
    if (!ranked.result) return {};
    const out: Record<string, string | null> = {};
    for (const lp of ranked.result.top_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.ribbon;
    }
    return out;
  }, [ranked.result, planById]);

  // The library's strict Gate 2 elimination means no plan in top_plans
  // or bench_plans carries an "unknown drug coverage" disclaimer; the
  // brain either confirmed covered or eliminated. Kept as a stub map
  // for CompareScreen prop compatibility.
  const drugCoverageUnknownByPlanId = useMemo<Record<string, boolean>>(
    () => ({}),
    [],
  );

  const drugsCoveredByPlanId = useMemo<Record<string, number>>(() => {
    if (!pricedResult) return {};
    const out: Record<string, number> = {};
    for (const lp of pricedResult.top_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.meds_covered;
    }
    for (const lp of [...pricedResult.bench_plans, ...(pricedResult.current_plan ? [pricedResult.current_plan] : [])]) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.meds_covered;
    }
    return out;
  }, [pricedResult, planById]);

  const drugsTotalByPlanId = useMemo<Record<string, number>>(() => {
    if (!pricedResult) return {};
    const out: Record<string, number> = {};
    for (const lp of pricedResult.top_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.meds_total;
    }
    for (const lp of [...pricedResult.bench_plans, ...(pricedResult.current_plan ? [pricedResult.current_plan] : [])]) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = lp.meds_total;
    }
    return out;
  }, [pricedResult, planById]);

  const drugBreakdownByPlanId = useMemo<
    Record<string, ReadonlyArray<DrugRow>>
  >(() => {
    if (!pricedResult) return {};
    const out: Record<string, ReadonlyArray<DrugRow>> = {};
    // Extra Help client: what they pay per fill for a covered drug is the
    // lesser of the plan's cost and the LIS maximum (generic vs brand) — the
    // same math the plan's drug total already uses (lis-cap-agent-v3), so a
    // single drug's price can never disagree with the plan total.
    const lisFill = (m: { covered: boolean; tier: number | null; name: string; annual_cost: number | null }): number | null => {
      if (!lisCopays || !m.covered || m.annual_cost == null) return null;
      const cap = lisCapClassFor({ tier: m.tier, drugName: m.name }) === 'brand' ? lisCopays.brand : lisCopays.generic;
      return Math.round(Math.min(m.annual_cost / 12, cap) * 100) / 100;
    };
    const lisAnnual = (perFill: number): number => Math.round(perFill * 12 * 100) / 100;
    const adapt = (
      meds: ReadonlyArray<{
        rxcui: string;
        name: string;
        covered: boolean;
        tier: number | null;
        copay: number | null;
        annual_cost: number;
        prior_auth?: boolean | null;
        step_therapy?: boolean | null;
        quantity_limit?: boolean | null;
        quantity_limit_amount?: number | null;
        quantity_limit_days?: number | null;
        monthly_cost?: number[] | null;
        switch_suggestion?: SwitchSuggestion | null;
        not_part_d?: boolean;
        part_b_eligible?: boolean;
        part_b_or_d?: boolean;
        part_b_note?: string | null;
      }>,
    ): DrugRow[] =>
      meds.map((m) =>
        formularyUnavailable
          ? {
              // Formulary not published → drop the rank-plans tier/cost entirely
              // rather than show a cross-year guess. Leaf renderers key off
              // costUnavailable to print "formulary not yet published".
              rxcui: m.rxcui,
              name: m.name,
              covered: false,
              tier: null,
              monthlyCopay: null,
              annualCost: 0,
              costUnavailable: true,
            }
          : {
              rxcui: m.rxcui,
              name: m.name,
              covered: m.covered,
              tier: m.tier,
              monthlyCopay: lisFill(m) ?? m.copay,
              annualCost: lisFill(m) != null ? lisAnnual(lisFill(m)!) : m.annual_cost,
              // PA / ST / QL flags from rank-plans (consumer PR #107) — the
              // Compare board's drug data comes from the rank-plans medications.
              priorAuth: m.prior_auth === true,
              stepTherapy: m.step_therapy === true,
              quantityLimit: m.quantity_limit === true,
              quantityLimitAmount: m.quantity_limit_amount ?? null,
              quantityLimitDays: m.quantity_limit_days ?? null,
              monthlyCost: lisFill(m) != null ? Array<number>(12).fill(lisFill(m)!) : m.monthly_cost ?? null,
              // Covered brand↔generic equivalent for an uncovered drug
              // (consumer rank-plans switch_suggestion). Non-null only when
              // !covered; drives the "ask your doctor about switching" note.
              switchSuggestion: m.switch_suggestion ?? null,
              // Part D / Part B coverage classification (rank-plans) — drives
              // the three verbatim drug-coverage labels on Compare / H2H.
              notPartD: m.not_part_d === true,
              partBEligible: m.part_b_eligible === true,
              partBOrD: m.part_b_or_d === true,
              partBNote: m.part_b_note ?? null,
            },
      );
    // Meds that never matched a drug code aren't sent to rank-plans, so
    // they'd vanish from every card (Cynthia Kline's fluticasone spray).
    // List them on each plan as unmatched instead of dropping them.
    const unmatched: DrugRow[] = medications
      .filter((m) => !m.rxcui)
      .map((m) => ({
        rxcui: '',
        name: m.name,
        covered: false,
        tier: null,
        monthlyCopay: null,
        annualCost: 0,
        unmatched: true,
      }));
    // Extra Help rows still stop at the yearly Part D out-of-pocket cap: once
    // the client's LIS copays add up to it, every later fill is $0. Applied
    // month by month across all her drugs so the per-drug rows, the Jan–Dec
    // calendar and the plan total (lis-cap-agent-v3, also capped) agree.
    const capRows = (rows: DrugRow[]): DrugRow[] => {
      if (!lisCopays) return rows;
      const months = rows.map((d) =>
        d.covered && Array.isArray(d.monthlyCost) ? [...d.monthlyCost] : null,
      );
      let spent = 0;
      for (let k = 0; k < 12; k++) {
        const due = months.reduce((sum, m) => sum + (m ? m[k] ?? 0 : 0), 0);
        const room = Math.max(0, PART_D_OOP_CAP - spent);
        if (due > room) {
          const f = due > 0 ? room / due : 0;
          for (const m of months) if (m) m[k] = Math.round((m[k] ?? 0) * f * 100) / 100;
        }
        spent += Math.min(due, room);
      }
      return rows.map((d, i) => {
        const m = months[i];
        if (!m) return d;
        return { ...d, monthlyCost: m, annualCost: Math.round(m.reduce((a, b) => a + b, 0) * 100) / 100 };
      });
    };
    for (const lp of pricedResult.top_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = [...capRows(adapt(lp.medications)), ...unmatched];
    }
    for (const lp of pricedResult.bench_plans) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p) out[p.id] = [...capRows(adapt(lp.medications)), ...unmatched];
    }
    // Current plan: priced separately (never ranked) so its card shows real
    // per-drug costs instead of "not priced".
    const cur = pricedResult.current_plan;
    if (cur) {
      const p = planById.get(normalizePlanId(cur.plan_id));
      if (p) out[p.id] = [...capRows(adapt(cur.medications)), ...unmatched];
    }
    return out;
  }, [pricedResult, planById, medications, lisCopays]);

  // Jan–Dec drug-cost calendar per plan (rank-plans drug_cost_by_month).
  // Withheld when the formulary isn't published — no real costs to lay out.
  const drugMonthsByPlanId = useMemo<Record<string, LibraryRankDrugMonth[]>>(() => {
    if (!pricedResult || formularyUnavailable) return {};
    const out: Record<string, LibraryRankDrugMonth[]> = {};
    const all = [
      ...pricedResult.top_plans,
      ...pricedResult.bench_plans,
      ...(pricedResult.current_plan ? [pricedResult.current_plan] : []),
    ];
    for (const lp of all) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (!p) continue;
      if (lisCopays) {
        // Extra Help: the server calendar is at full plan cost. Rebuild it
        // from the LIS-capped per-drug fills so each month shows what the
        // client actually pays.
        const rows = drugBreakdownByPlanId[p.id] ?? [];
        if (!rows.some((d) => d.covered && Array.isArray(d.monthlyCost))) continue;
        out[p.id] = Array.from({ length: 12 }, (_, k) => ({
          month: k + 1,
          cost:
            Math.round(
              rows.reduce(
                (sum, d) => sum + (d.covered && Array.isArray(d.monthlyCost) ? d.monthlyCost[k] ?? 0 : 0),
                0,
              ) * 100,
            ) / 100,
          phase: 'initial' as 'initial' | 'catastrophic',
        }));
        // Months after the out-of-pocket cap is reached are catastrophic ($0).
        let run = 0;
        for (const mo of out[p.id]) {
          if (run >= PART_D_OOP_CAP) mo.phase = 'catastrophic';
          run += mo.cost;
        }
      } else if (lp.drug_cost_by_month && lp.drug_cost_by_month.length === 12) {
        out[p.id] = lp.drug_cost_by_month;
      }
    }
    return out;
  }, [pricedResult, planById, formularyUnavailable, lisCopays, drugBreakdownByPlanId]);

  // Whole-plan yearly cost per plan (premium + drugs − giveback + expected
  // medical copays) — the Head to Head winner is decided on this.
  const wholeCostByPlanId = useMemo<Record<string, number>>(() => {
    if (!pricedResult) return {};
    const out: Record<string, number> = {};
    for (const lp of [
      ...pricedResult.top_plans,
      ...pricedResult.bench_plans,
      ...(pricedResult.current_plan ? [pricedResult.current_plan] : []),
    ]) {
      const p = planById.get(normalizePlanId(lp.plan_id));
      if (p && typeof lp.total_annual_cost_with_medical === 'number') {
        // Swap rank-plans' full-cost drug total for the Extra Help-capped one
        // (same number the Drug cost row shows) so an LIS client's winner is
        // decided on what she actually pays. Unchanged when there's no LIS.
        const capped = annualDrugByPlanId[p.id];
        out[p.id] =
          capped != null
            ? lp.total_annual_cost_with_medical - lp.total_annual_drug_cost + capped
            : lp.total_annual_cost_with_medical;
      }
    }
    return out;
  }, [pricedResult, planById, annualDrugByPlanId]);

  // ── Per-plan gate explanations (CompareScreen "Why this plan" pills) ─
  // Sourced from the LOCAL brain (usePlanBrain), not the library response.
  // The library's LibraryRankPlan shape doesn't yet carry gate_explanations
  // — it has gate_results booleans only. usePlanBrain produces the same
  // BrainScore.explanations strings the consumer Results screen renders
  // (packages/brain/src/plan-brain.ts), so reading them off `brain.result`
  // by plan_id keeps the two surfaces phrasing-identical without waiting
  // on a library-server change. Walks both scored + bench so every county
  // plan in CompareScreen's grid + bench has a "Why this plan" expander.
  // dualEligibleByPlanId is destructured above alongside annualDrugByPlanId
  // — both come from buildAgentV3LisMaps() which reads library rank output
  // and applies LIS caps client-side. The legacy usePlanBrain-fed
  // dualEligibleByPlanId path was removed here: usePlanBrain doesn't run
  // in the agent-v3 rank/compare flow, so the previous implementation
  // was inert (empty object) whenever Compare rendered.

const explanationsByPlanId = useMemo<
    Record<
      string,
      {
        gate1: ReadonlyArray<string>;
        gate2: ReadonlyArray<string>;
        gate3: ReadonlyArray<string>;
        gate4: string;
      }
    >
  >(() => {
    if (!brain.result) return {};
    const out: Record<
      string,
      {
        gate1: ReadonlyArray<string>;
        gate2: ReadonlyArray<string>;
        gate3: ReadonlyArray<string>;
        gate4: string;
      }
    > = {};
    for (const sp of brain.result.scored) out[sp.plan.id] = sp.explanations;
    for (const sp of brain.result.bench) out[sp.plan.id] = sp.explanations;
    return out;
  }, [brain.result]);

  // Composite ranking score per plan id — fed to CompareScreen so the
  // v2 SlotCell can render a normalized confidence % (this-plan's
  // composite / best-in-current-pool composite). Same {scored∪bench}
  // union pattern as explanationsByPlanId.
  const compositeByPlanId = useMemo<Record<string, number>>(() => {
    if (!brain.result) return {};
    const out: Record<string, number> = {};
    for (const sp of brain.result.scored) out[sp.plan.id] = sp.composite;
    for (const sp of brain.result.bench) out[sp.plan.id] = sp.composite;
    return out;
  }, [brain.result]);

  // ── Current plan lookup ──────────────────────────────────────────
  // Normalize both sides so an AgentBase 2-part id ("H5253-117") or a
  // 3-char-segment id ("S4802-143-000") still resolves to the pool row
  // ("H5253-117-0" / "S4802-143-0") that pm_plans emits via /api/plans.
  // Rob's 2026-08-09 audit found ~76% of AgentBase clients with a
  // non-empty plan_id missed the strict === lookup for pure format
  // reasons; the plate above the board (e127d01) keeps covering the
  // residual malformed / sanctioned / non-commissionable / prior-year
  // cases. `currentPlan` is only consumed downstream by CompareScreen
  // (baseline column + delta arrows) and EnrollScreen (annual-cost
  // benchmark); the brain's ranking pipeline receives `currentPlanId`
  // directly, so this change cannot affect ranking, gates, or which
  // plans enter the pool.
  const currentPlan = useMemo<Plan | null>(() => {
    if (!currentPlanId) return null;
    const target = normalizePlanId(currentPlanId);
    return eligiblePlans.find((p) => normalizePlanId(p.id) === target) ?? null;
  }, [currentPlanId, eligiblePlans]);

  // ── Brain-ranked plans (scored ∪ bench) in ranking order ─────────
  // Source of truth for what the Compliance / Enroll gate is allowed to
  // save. Anything not in this set is not a valid recommendation — the
  // brain excludes the incumbent (and other non-shoppable plans), and
  // /api/agentbase-recommend expects the picked plan to have a matching
  // ScoredPlan entry in brainResult so the CMS audit payload
  // (ribbon, applied rules, red flags, real annual cost) can be built.
  // Fed to ComplianceScreen + EnrollScreen so their resolution can't
  // silently fall back to the library's ranked list (which used to
  // surface the incumbent as slot 0) or the current-plan slot.
  const brainRankedPlans = useMemo<Plan[]>(() => {
    if (!brain.result) return [];
    return [...brain.result.scored, ...brain.result.bench].map((s) => s.plan);
  }, [brain.result]);

  // ── Compliance progress (real, against canonical 16) ─────────────
  const complianceTotal = totalComplianceItems();
  const complianceDone = new Set(checked).size + new Set(confirmed).size;
  const complianceProgress = (complianceDone / complianceTotal) * 100;

  function onCycleShare() {
    if (shareStarting) return;
    if (shareActive) {
      void stopShare('agent-v3 share toggle');
    } else {
      // Fail fast when the broker hasn't captured a phone yet — the API
      // would 400 with "clientPhone required" and the UI would silently
      // surface no SMS. Surfacing an alert is intentionally crude:
      // share is a deliberate, in-call action, not a background hum.
      if (!client.phone || client.phone.trim() === '') {
        window.alert('Add a client phone on the Client screen first — the SMS link can\'t go anywhere without it.');
        return;
      }
      void startShare({
        clientPhone: client.phone,
        clientFirstName: client.name.split(' ')[0] || undefined,
      });
    }
  }

  function togglePriority(key: PriorityKey) {
    setPriorities((curr) =>
      curr.includes(key) ? curr.filter((k) => k !== key) : [...curr, key],
    );
  }

  // Pick order drives Gate 3 relax order on the brain side (bottom of
  // the list relaxes first when no county plans satisfy every pick).
  function movePriority(key: PriorityKey, direction: 'up' | 'down') {
    setPriorities((curr) => {
      const i = curr.indexOf(key);
      if (i < 0) return curr;
      const swapWith = direction === 'up' ? i - 1 : i + 1;
      if (swapWith < 0 || swapWith >= curr.length) return curr;
      const next = [...curr];
      [next[i], next[swapWith]] = [next[swapWith], next[i]];
      return next;
    });
  }

  // AgentBase write-back. CompareScreen fires this fire-and-forget;
  // EnrollScreen awaits the returned promise so it can show a toast
  // and only open SunFire on a 2xx. The endpoint is idempotent for
  // same-plan re-clicks (handled inside useAgentBaseRecommend.sync).
  //
  // When the optional snapshot is passed (EnrollScreen path), the
  // compliance + sessionSummary fields flow through to the recommend
  // endpoint, which stamps clients.soa_confirmed_at /
  // call_recording_disclosed_at and inserts a planmatch_activity_log
  // row for the CMS audit trail.
  async function onRecommend(
    plan: Plan,
    snapshot?: {
      compliance?: ComplianceSnapshot;
      sessionSummary?: AgentV3SessionSummary;
    },
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    // Compliance gate: never write a non-sellable plan to AgentBase. The
    // library brain is authoritative (UHC/Devoted flags, exclude rules,
    // Wellcare allowlist). Blocks the enroll before any sync input is
    // built, so nothing reaches /api/agentbase-recommend.
    if (librarySellableByAgentId.get(plan.id) === false) {
      return {
        ok: false,
        error:
          `${plan.carrier ?? ''} ${plan.plan_name} is not sellable — Rob can't enroll this plan. ` +
          `The client can enroll on Medicare.gov or by calling 1-800-MEDICARE.`,
      };
    }
    const idParam = getClientIdParam();
    const agentbaseClientId =
      idParam && /^\d+$/.test(idParam) ? Number(idParam) : null;
    // Rotate session_token per Enroll click so every enrollment gets
    // its own row in planmatch_sessions (its own ⚡ queue entry Rob
    // can approve/reject independently). Before this, one token per
    // CompareScreen mount meant enrolling plan A then plan B upserted
    // one row and B silently overwrote A.
    //
    // Single-click idempotence is preserved by useAgentBaseRecommend's
    // syncedPlanIdRef.current === planId early-return: two clicks on
    // the SAME plan within the same hook instance short-circuit the
    // second call, so a double-click can't create two rows for the
    // same plan.
    //
    // The store's `sessionId` (useSession) is still valid as a
    // per-mount identifier for other uses, but the outbound
    // session_token is now per-enroll to give each enrollment its own
    // life in the CRM queue.
    // Token shape: <sessionId>__<planId>__<clickId>. The double-
    // underscore delimiter avoids collision with the ses_XXX prefix
    // useSession assigns, and the clickId is Date+random so a fast
    // second click on a different plan still yields a distinct row.
    const clickId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const perEnrollToken = `${sessionId}__${plan.id}__${clickId}`;
    const input = await buildAgentV3SyncInput({
      plan,
      client,
      medications,
      providers,
      brainResult: brain.result,
      sessionId: perEnrollToken,
      startedAt,
      agentbaseClientId,
      compliance: snapshot?.compliance,
      sessionSummary: snapshot?.sessionSummary,
    });
    if (!input) {
      // Diagnostic: dump which side failed so a repro pastes the plan
      // id + brain state directly. Prior version only logged the
      // catch-all message which forced a code-dive to debug.
      console.warn('[agent-v3] recommend skipped — buildAgentV3SyncInput returned null', {
        planId: plan.id,
        planCarrier: plan.carrier,
        brainReady: brain.result != null,
        scoredIds: brain.result?.scored.map((s) => s.plan.id) ?? null,
        benchIds: brain.result?.bench.map((s) => s.plan.id) ?? null,
      });
      const reason = !brain.result
        ? 'Brain ranking not ready — try again in a moment.'
        : `Plan ${plan.carrier} ${plan.plan_name} isn't in the brain-ranked set — re-run Compare.`;
      return { ok: false, error: reason };
    }
    const r = await agentbaseSync.sync(input);
    return r.ok ? { ok: true } : { ok: false, error: r.error };
  }

  return (
    <div className="pma3">
      <style>{AGENT_V3_CSS}</style>

      <AgentBar
        screen={screen}
        onNav={setScreen}
        clientView={clientView}
        onToggleView={() => setClientView((v) => !v)}
        shareOn={shareActive}
        shareStarting={shareStarting}
        shareSmsFailed={Boolean(shareResult?.smsFailed)}
        shareSmsTo={shareResult?.smsTo ?? null}
        shareError={shareError}
        shareLink={shareResult?.link ?? null}
        onCycleShare={onCycleShare}
        complianceProgress={complianceProgress}
      />

      {brain.error && (
        <div
          style={{
            margin: '8px 0',
            padding: '10px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#7f1d1d',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⚠ Plan Brain data unavailable ({brain.error}). The ranking would be
          unreliable without network/formulary/benefit data, so it is not
          shown. Refresh or check the API logs.
        </div>
      )}
      {/* Library rank-plans failure. Fires on any error — first-load
          (no previous result) or stale (last result still rendered)
          alike. Without this, a 5xx / AbortError on cold start leaves
          providers as "⚠ Unverified" with no plans rendered and no
          on-screen explanation. */}
      {ranked.error && (
        <div
          style={{
            margin: '8px 0',
            padding: '10px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#7f1d1d',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⚠ Plan ranking failed ({ranked.error}).
          {ranked.result
            ? ' Showing the last successful result — refresh to retry.'
            : ' Refresh to retry, or check the Library API logs.'}
        </div>
      )}
      {brain.unresolvedProviderNames.length > 0 && (
        <div
          style={{
            margin: '8px 0',
            padding: '10px 12px',
            background: '#fff8e1',
            border: '1px solid #f5d479',
            borderRadius: 8,
            color: '#7a5b00',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          ⚠ Provider NPI could not be resolved for:{' '}
          {brain.unresolvedProviderNames.join(', ')} — network status unknown
          for all plans. Re-pick from the Providers search or add an NPI so
          Gate 1 can run against the real network.
        </div>
      )}

      <div>
        {screen === 'intake' && (
          <IntakeScreen
            eligiblePlans={eligiblePlans}
            presetCurrentPlanLabel={presetCurrentPlanLabel}
            capture={capture}
            onNext={() => setScreen('disclaimers')}
          />
        )}
        {screen === 'disclaimers' && (
          <DisclaimersScreen
            eligiblePlans={eligiblePlans}
            onBack={() => setScreen('intake')}
            onNext={() => setScreen('meds')}
          />
        )}
        {screen === 'meds' && (
          <MedsScreen
            clientView={clientView}
            capture={capture}
            formularyUnavailable={formularyUnavailable}
            unpublishedPlanYear={unpublishedPlanYear}
            onBack={() => setScreen('disclaimers')}
            onNext={() => setScreen('providers')}
          />
        )}
        {screen === 'providers' && (
          <ProvidersScreen
            clientView={clientView}
            rankedPlanIds={rankedPlanIds}
            capture={capture}
            onBack={() => setScreen('meds')}
            onNext={() => setScreen('priorities')}
          />
        )}
        {screen === 'priorities' && (
          <PrioritiesScreen
            selected={priorities}
            onToggle={togglePriority}
            onMove={movePriority}
            onBack={() => setScreen('providers')}
            onNext={() => setScreen('compare')}
          />
        )}
        {screen === 'compare' && (
          <CompareScreen
            current={currentPlan}
            scoredPlans={scoredPlans}
            benchPlans={benchPlans}
            benchGateResultsByPlanId={benchGateResultsByPlanId}
            ribbonByPlanId={ribbonByPlanId}
            annualDrugByPlanId={annualDrugByPlanIdForDisplay}
            drugCostsUnavailable={formularyUnavailable}
            unpublishedPlanYear={unpublishedPlanYear}
            drugCoverageUnknownByPlanId={drugCoverageUnknownByPlanId}
            drugsCoveredByPlanId={drugsCoveredByPlanId}
            drugsTotalByPlanId={drugsTotalByPlanId}
            drugBreakdownByPlanId={drugBreakdownByPlanId}
            drugMonthsByPlanId={drugMonthsByPlanId}
            wholeCostByPlanId={wholeCostByPlanId}
            explanationsByPlanId={explanationsByPlanId}
            compositeByPlanId={compositeByPlanId}
            unresolvedDrugs={brain.result?.unresolvedDrugs ?? []}
            dualEligibleByPlanId={dualEligibleByPlanId}
            rankedPlans={
              ranked.result
                ? [...ranked.result.top_plans, ...ranked.result.bench_plans]
                : undefined
            }
            priorities={priorities}
            pharmacy={pharmacyChannel}
            onPharmacyChange={setPharmacyChannel}
            onRecommend={onRecommend}
            onBack={() => setScreen('priorities')}
            onNext={() => setScreen('compliance')}
          />
        )}
        {screen === 'compliance' && (
          <ComplianceScreen
            brainRankedPlans={brainRankedPlans}
            annualDrugByPlanId={annualDrugByPlanIdForDisplay}
            onRecommend={onRecommend}
            onBack={() => setScreen('compare')}
            onNext={() => setScreen('enroll')}
          />
        )}
        {screen === 'enroll' && (
          <EnrollScreen
            current={currentPlan}
            brainRankedPlans={brainRankedPlans}
            onRecommend={onRecommend}
            annualDrugByPlanId={annualDrugByPlanIdForDisplay}
            onBack={() => setScreen('compliance')}
          />
        )}
        {screen === 'dental' && <DentalScreen />}
      </div>

      {/* AgentBase hydration banner — only renders for clientId loads.
          Loading: faint top-right toast. Error: red, sticky until the
          broker dismisses (manual close = "I'll keep going without
          CRM data"). Ready state stays mounted as a small "loaded
          from AgentBase · <name>" badge so the broker can confirm
          they're looking at the right person. */}
      {hydration.kind !== 'idle' && (
        <HydrationToast
          state={hydration}
          onDismiss={() => setHydration({ kind: 'idle' })}
        />
      )}

      {/* Pool-integrity banners — hard-fails when the plan pool didn't
          reach the county universe. Either state is a session-blocker:
          without a real pool the bench renders the wrong plans, so the
          banner is sticky and unmissable. Placed as a top strip so it
          shows across every screen, not just Compare. See Phase 1.10. */}
      {/* Not on Dental: that screen doesn't use the MA county pool, and
          the fixed banner sits over its inputs. */}
      {screen !== 'dental' && (poolMissingGeo || poolTruncated) && (
        <PoolIntegrityBanner
          missingGeo={poolMissingGeo}
          truncated={poolTruncated}
          clientState={client.state}
          clientZip={client.zip}
        />
      )}

      {/* Bottom-left tag so reviewers know which build they're in,
          with the broker / CMS-not-reviewed disclaimer beneath. Agent
          v3 is internal broker tooling, not a consumer surface, but
          screen-shares + recordings expose the chrome to consumers so
          the marketing-attribution + CMS-not-reviewed clause carries
          through. */}
      <div
        style={{
          position: 'fixed',
          bottom: 12,
          left: 12,
          maxWidth: 320,
          color: 'rgba(13,47,94,0.6)',
          background: 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(4px)',
          padding: '6px 10px',
          borderRadius: 6,
          border: '1px solid rgba(13,47,94,0.08)',
          fontFamily:
            'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'rgba(13,47,94,0.5)',
          }}
        >
          Agent v3 · review build
        </div>
        <div style={{ fontSize: 9, lineHeight: 1.4, marginTop: 4 }}>
          GenerationHealth.me · NPN 10447418. Plan data is sourced from
          CMS public files and has not been reviewed by CMS or any
          Medicare plan. Official info: Medicare.gov or
          1-800-MEDICARE (TTY 1-877-486-2048).
        </div>
      </div>
    </div>
  );
}

interface HydrationToastProps {
  state: Exclude<HydrationState, { kind: 'idle' }>;
  onDismiss: () => void;
}

function HydrationToast({ state, onDismiss }: HydrationToastProps) {
  const base = {
    position: 'fixed' as const,
    top: 12,
    right: 12,
    zIndex: 200,
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    maxWidth: 360,
  };
  if (state.kind === 'loading') {
    return (
      <div
        style={{
          ...base,
          background: 'rgba(13,47,94,0.92)',
          color: '#83f0f9',
          border: '1px solid rgba(131,240,249,0.3)',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#83f0f9',
            animation: 'pma3-pulse 1.2s ease-in-out infinite',
          }}
        />
        Loading client #{state.clientId} from AgentBase…
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div
        role="alert"
        style={{
          ...base,
          background: '#7f1d1d',
          color: '#fecaca',
          border: '1px solid #ef4444',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <strong>AgentBase load failed</strong> for client #{state.clientId}.
        </div>
        <div style={{ fontWeight: 400, opacity: 0.85 }}>{state.message}</div>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 4,
            padding: '2px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: 'inherit',
            cursor: 'pointer',
            alignSelf: 'flex-end',
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }
  // ready
  return (
    <div
      style={{
        ...base,
        background: 'rgba(6,78,59,0.92)',
        color: '#a7f3d0',
        border: '1px solid #34d399',
      }}
    >
      <span aria-hidden>✓</span>
      Loaded {state.clientName} from AgentBase
      <button
        type="button"
        onClick={onDismiss}
        title="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          fontSize: 14,
          lineHeight: 1,
          padding: 0,
          marginLeft: 4,
        }}
      >
        ✕
      </button>
    </div>
  );
}

interface PoolIntegrityBannerProps {
  missingGeo: { reason: 'no-state' | 'no-county' } | null;
  truncated: boolean;
  clientState: StateCode | null;
  clientZip: string;
}

function PoolIntegrityBanner({
  missingGeo,
  clientState,
  clientZip,
}: PoolIntegrityBannerProps) {
  // Missing geo takes precedence — no pool at all is a worse state than
  // a truncated pool. Both are sticky, no dismiss: silently rendering
  // the wrong pool is exactly the failure mode the banner fixes.
  const isError = missingGeo != null;
  const bg = isError ? '#7f1d1d' : '#78350f';
  const border = isError ? '#ef4444' : '#f59e0b';
  const fg = isError ? '#fecaca' : '#fed7aa';
  let msg: string;
  if (missingGeo?.reason === 'no-state') {
    msg = 'Client has no state on file — bench can\'t load a county plan pool. Set the state on Intake.';
  } else if (missingGeo?.reason === 'no-county') {
    msg = clientZip
      ? `Client has no county on file. Resolving from ZIP ${clientZip}…`
      : 'Client has no county or ZIP on file — bench can\'t load a plan pool. Set the county or ZIP on Intake.';
  } else {
    // truncated
    msg =
      `Bench pool is truncated${clientState ? ` for ${clientState}` : ''}` +
      ' — the /api/plans row cap fired and the pool is a partial county universe. Every dropdown count understates. Reload the county or narrow the limit.';
  }
  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 210,
        maxWidth: 720,
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        borderRadius: 8,
        padding: '10px 14px',
        fontFamily: 'Inter, -apple-system, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 6px 20px rgba(0,0,0,0.28)',
      }}
    >
      <span aria-hidden style={{ marginRight: 8 }}>{isError ? '⚠' : '⚡'}</span>
      {msg}
    </div>
  );
}
